<template>
    <v-card>
        <v-card-title>
            <v-row>
                <v-col class="py-0">총 입금금액 : {{ totalPaymentAmount.format() }}원 / {{ totalPlanAmount.format() }}원</v-col>
                <v-col class="py-0">정산계좌 : {{ settlementAccount }} </v-col>
            </v-row>
        </v-card-title>
        <v-card-text>
            <v-data-table :items="paymentsWithSettlement" :headers="headers" disable-sort hide-default-footer>
                <template v-slot:[`item.wiredAt`]="{ item }"> {{ item.wiredAt.toDateTime() }} </template>
                <template v-slot:[`item.amount`]="{ item }"> {{ item.amount.format() }} 원 </template>
                <template v-slot:[`item.type`]="{ item }"> {{ item.type }} {{ item.isManual ? "(수동처리)" : "" }} </template>
                <template v-slot:[`item.taxProof`]="{ item }">
                    <v-row v-if="item.taxProof">
                        <v-col class="text-caption">
                            {{ item.taxProof.type }} 발행 완료
                            <template v-if="item.taxProof.respondData.confirmNum">
                                <br />
                                승인번호 : {{ item.taxProof.respondData.confirmNum }}
                            </template>
                        </v-col>
                    </v-row>
                    <v-row v-else align="center">
                        <v-col cols="auto">미발행</v-col>
                        <v-col v-if="isConsole" cols="auto"><v-btn small @click="$refs['proof-issuance'].open(item)">증빙발행</v-btn></v-col>
                    </v-row>
                </template>
                <template v-slot:[`item.commissionPercentage`]=""> {{ commissionPercentage }} % </template>
                <template v-slot:[`item.settlement`]="{ item }">
                    {{ item.settlement.amount.format() + "원" }}
                    <template v-if="item.settlement.commission">(수수료 {{ item.settlement.commission.format() }}원 제외)</template>
                </template>
                <template v-slot:[`item.settlementStatus`]="{ item }">
                    <template v-if="item.isSettled">
                        <template v-if="item.isManual">{{ item.settlement.settledAt.toDateTime() }}</template>
                        <template v-else>{{ item.manual.settlement.settledAt.toDateTime() }}</template>
                    </template>
                    <v-row v-else align="center">
                        <v-col cols="auto">미정산</v-col>
                        <v-col v-if="!item.isManual && isConsole" cols="auto"><v-btn small @click="settle(item)">정산처리</v-btn></v-col>
                    </v-row>
                </template>
            </v-data-table>
        </v-card-text>
        <v-card-actions v-if="isConsole">
            <v-row justify="center">
                <v-col cols="auto">
                    <v-btn @click="$refs['manual-payments'].open()" color="secondary">
                        수동항목관리
                        <v-icon class="ml-2">mdi-clipboard-text</v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-actions>
        <proof-issuance v-model="value" ref="proof-issuance" />
        <manual-payments v-model="value" :payments="paymentsWithSettlement" :commissionPercentage="commissionPercentage" ref="manual-payments" />
    </v-card>
</template>

<script>
import api from "@/api";
import ProofIssuance from "./proof-issuance.vue";
import banks from "@/assets/data/banks.json";
import ManualPayments from "./manual-payments.vue";
import dayjs from "dayjs";

export default {
    components: { ProofIssuance, ManualPayments },
    props: {
        value: {
            _id: String,
            isWhole: Boolean,
            virtualAccount: {
                transactions: Array,
            },
            payments: { type: Array, default: () => [] },
        },
        paymentPlan: { type: Array, default: () => [] },
    },
    data() {
        return {
            headers: [
                { text: "입금일시", value: "wiredAt", width: 120 },
                { text: "입금액", value: "amount", width: 140 },
                { text: "적요", value: "message", width: 140 },
                { text: "결제형태", value: "type", width: 110 },
                { text: "증빙발행", value: "taxProof", width: 180 },
                { text: "정산수수료율", value: "commissionPercentage", width: 110 },
                { text: "정산금액", value: "settlement", width: 160 },
                { text: "정산완료", value: "settlementStatus", width: 210 },
                { text: "비고", value: "comment" },
            ],
        };
    },
    computed: {
        isConsole() {
            return this.$store.state.payload.scope.includes("console");
        },
        totalPaymentAmount() {
            return this.value.payments.reduce((total, { amount }) => total + amount, 0);
        },
        totalPlanAmount() {
            return this.paymentPlan.reduce((total, { amount }) => total + amount, 0);
        },
        commissionPercentage() {
            return (this.value.isWhole ? 4 : 8).toFixed(2, 0);
        },
        settlementAccount() {
            const { bankCode, bankAccount, bankAccountName } = this.value.latestDocument.employee;

            if (bankCode && bankAccount && bankAccountName) {
                const bankName = banks.find(({ code }) => code == bankCode).name;
                return `${bankName} / ${bankAccount} (예금주 : ${bankAccountName})`;
            } else return "정산계좌가 없습니다";
        },
        paymentsWithSettlement() {
            return JSON.parse(JSON.stringify(this.value.payments))
                .sort((a, b) => (dayjs(a.wiredAt).isAfter(b.wiredAt) ? 1 : -1))
                .map(({ settlement, ...payment }, index) => ({
                    ...payment,
                    settlement: settlement ? settlement : this.getSettlementEstimate(payment, index),
                    comment: null,
                }));
        },
    },
    methods: {
        async settle({ _id: _payment, settlement: data }) {
            const go = confirm(`정산 처리 시 자동으로 ${data.amount.format()}원이 송금됩니다. 진행하시겠습니까?`);
            if (go) {
                try {
                    const { settlement } = await api.console.contracts.settlement.post({ _contract: this.value._id, _payment, data });
                    const target = this.value.payments.find(({ _id }) => _id == _payment);
                    Object.assignDefined(target, { _settlement: settlement._id, settlement });
                    alert("정산이 처리되었습니다.");
                } catch (error) {
                    this.handleError(error);
                }
            }
        },
        handleError(error) {
            console.error(error);
            alert(error.response ? error.response.data.message : error.message);
        },
        getSettlementEstimate({ amount }, index) {
            switch (index) {
                case 0:
                    return (() => {
                        const commission = Math.round(this.totalPlanAmount * this.commissionPercentage * 0.01);
                        return { commission, amount: amount - commission };
                    })();
                default:
                    return { amount };
            }
        },
    },
};
</script>

<style></style>
